// src/pages/admin/UsersList.tsx

import { Fragment, useState } from "react";
import { Container, Col, Row, Card, Table, Badge, Alert } from "react-bootstrap";
import useUsersDataList, { UserDataItem } from "hooks/useUsersDataList";
import AddUserModal from "components/admin/AddUserModal";
import EditUserModal from "components/admin/EditUserModal";
// import http from "services/http";
import http from "services/httpClient";

export const UsersList = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const users = useUsersDataList(refreshKey);

  const [showModal, setShowModal] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDataItem | null>(null);
  
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<string>("success");

  const flash = (msg: string, variant: string = "success") => {
    setAlertMessage(msg);
    setAlertVariant(variant);
    setTimeout(() => setAlertMessage(null), 3000);
  };

  const handleSaveUser = async (email: string, role: string) => {
    try {
      const res = await http.post(`/auth/register`, { email, role });
      if (res.status === 201) {
        flash("User successfully created!", "success");
        setShowModal(false);
        setRefreshKey((k) => k + 1);
      } else {
        flash(`Unexpected response: ${res.status}`, "warning");
      }
    } catch (err: any) {
      // console.error("Failed to add user:", err);
      if (err?.response?.status === 409) {
        flash("User already exists!", "danger");
      } else {
        flash("Failed to add user. Please try again.", "danger");
      }
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
  
    try {
      await http.delete(`/users/${id}`);
      flash("User deleted successfully", "success");
      setRefreshKey((prev) => prev + 1); // ✅ reload the list
    } catch (err) {
      flash("Failed to delete user. Please try again.", "danger");
    }
  };  

  const handleEditUser = (user: UserDataItem) => {
    setSelectedUser(user);
    setEditModalShow(true);
  };

  const handleSaveUserEdit = async (id: number, role: string, isActive: boolean) => {
    try {
      await http.patch(`/users/${id}`, { role, is_active: isActive });
      flash("User updated successfully", "success");
      setRefreshKey((prev) => prev + 1); // ✅ reload user list
    } catch (err) {
      // console.error("Failed to update user:", err);
      flash("Failed to update user. Please try again.", "danger");
    }
  };

  return (
    <Fragment>
      <div className="bg-danger pt-5 pb-21"></div>
      <Container fluid className="mt-n22 px-6">
        <Row className="mt-6">
          <Col md={12} xs={12}>
          <Card className="rounded-3 p-3 shadow-sm">
            <Card.Header className="bg-white py-4 d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Users</h4>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowModal(true)}
              >
                Add User
              </button>
            </Card.Header>
              <Table responsive className="text-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Active</th>
                    <th>Verified</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user: UserDataItem) => (
                    <tr key={user.id}>
                    <td className="align-middle">{user.email}</td>
                    <td className="align-middle text-capitalize">{user.role}</td>
                    <td className="align-middle">
                      <Badge bg={user.is_active ? "success" : "secondary"}>
                        {user.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="align-middle">
                      <Badge bg={user.is_verified ? "success" : "secondary"}>
                        {user.is_verified ? "Verified" : "Not Verified"}
                      </Badge>
                    </td>
                    <td className="align-middle">
                      {new Date(user.date_created).toLocaleString()}
                    </td>
                    <td className="align-middle">
                    {user.role !== "owner" && (
                      <>
                        <button
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    </td>
                  </tr>              
                  ))}
                </tbody>
              </Table>
              <EditUserModal
                show={editModalShow}
                onHide={() => setEditModalShow(false)}
                onSave={handleSaveUserEdit}
                user={selectedUser}
              />
              <AddUserModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSave={handleSaveUser}
              />
              <div>
                {alertMessage && (
                  <Alert
                    variant={alertVariant}
                    onClose={() => setAlertMessage(null)}
                    dismissible
                  >
                    {alertMessage}
                  </Alert>
                )}
                {/* ... the rest of your UsersList (table, modal, etc.) */}
              </div>
              {/* <Card.Footer className="bg-white text-center">
                <Link to="#" className="link-primary">
                  View All Users
                </Link>
              </Card.Footer> */}
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UsersList;
